import { supabase } from "@/integrations/supabase/client";

const BUCKET = "media";
const TEN_YEARS = 60 * 60 * 24 * 365 * 10;

function randomName(file: File) {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "jpg";
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}.${ext}`;
}

/** Compress large images in the browser before uploading. */
export async function compressImage(file: File, maxWidth = 1920, quality = 0.82): Promise<File> {
  if (typeof window === "undefined") return file;
  if (!file.type.startsWith("image/") || file.type === "image/gif" || file.type === "image/svg+xml") {
    return file;
  }
  // PNGs may carry transparency — never flatten them into JPEG (black background).
  const keepsAlpha = file.type === "image/png" || file.type === "image/webp";
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxWidth / bitmap.width);
    if (scale === 1 && (keepsAlpha || file.size < 900_000)) return file;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const outType = keepsAlpha ? file.type : "image/jpeg";
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, outType, quality));
    if (!blob || blob.size >= file.size) return file;
    const ext = keepsAlpha ? (file.type === "image/png" ? ".png" : ".webp") : ".jpg";
    return new File([blob], file.name.replace(/\.\w+$/, ext), { type: outType });
  } catch {
    return file;
  }
}


export async function uploadMedia(file: File, folder = "geral"): Promise<string> {
  const prepared = await compressImage(file);
  const path = `${folder}/${randomName(prepared)}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, prepared, {
    cacheControl: "31536000",
    upsert: false,
  });
  if (error) throw error;

  const { data, error: signError } = await supabase.storage.from(BUCKET).createSignedUrl(path, TEN_YEARS);
  if (signError || !data?.signedUrl) throw signError ?? new Error("Não foi possível gerar o link do arquivo.");
  return data.signedUrl;
}

export function storagePathFromUrl(url: string): string | null {
  const match = url.match(/\/storage\/v1\/object\/sign\/media\/([^?]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export async function removeMedia(url: string) {
  const path = storagePathFromUrl(url);
  if (!path) return;
  await supabase.storage.from(BUCKET).remove([path]);
}
